/* CPU catalogue refinements. They narrow the model picker without choosing a CPU for the customer. */
((scope) => {
  'use strict';
  const groups = [['manufacturer', 'CPU manufacturer'], ['family', 'CPU family'], ['series', 'CPU series']];
  const text = value => String(value || '').trim();
  const compare = (a, b) => a.localeCompare(b, 'en-GB', { numeric: true, sensitivity: 'base' });

  function classify(option) {
    const label = text(option.label || option.title || option.value);
    const brand = `${text(option.brand || option.meta?.brand)} ${label}`;
    const manufacturer = /\bAMD\b|\bRyzen\b|\bThreadripper\b|\bAthlon\b/i.test(brand) ? 'AMD'
      : /\bIntel\b|\bCore\b|\bPentium\b|\bCeleron\b/i.test(brand) ? 'Intel' : 'Other';
    let family = 'Other CPUs', series = 'Other series';
    const ryzen = label.match(/\bRyzen\s+([3579])\s+(?:PRO\s+)?(\d{4,5})/i);
    const ryzenAi = label.match(/\bRyzen\s+AI\s+([3579])\s+(?:(?:PRO|HX)\s+)*(\d{3})/i);
    const threadripper = label.match(/\bThreadripper\s+(PRO\s+)?(\d{4})/i);
    const ultra = label.match(/\bCore\s+Ultra\s+([3579])\s+(\d{3})/i);
    const core = label.match(/\bCore\s+i([3579])[-\s]+(\d{4,5})/i);
    const coreNew = label.match(/\bCore\s+([3579])\s+(\d{3})/i);
    if (ryzen) {
      family = `Ryzen ${ryzen[1]}`;
      series = `AMD ${Math.floor(Number(ryzen[2]) / 1000) * 1000} Series`;
    } else if (ryzenAi) {
      family = `Ryzen AI ${ryzenAi[1]}`;
      series = `AMD Ryzen AI ${Math.floor(Number(ryzenAi[2]) / 100) * 100} Series`;
    } else if (threadripper) {
      family = `Threadripper${threadripper[1] ? ' PRO' : ''}`;
      series = `AMD Threadripper ${Math.floor(Number(threadripper[2]) / 1000) * 1000} Series`;
    } else if (ultra) {
      family = `Core Ultra ${ultra[1]}`;
      series = `Intel Core Ultra ${Math.floor(Number(ultra[2]) / 100) * 100} Series`;
    } else if (core) {
      family = `Core i${core[1]}`;
      const generation = Number(core[2].slice(0, core[2].length - 3));
      const suffix = generation % 100 >= 11 && generation % 100 <= 13 ? 'th'
        : ({ 1: 'st', 2: 'nd', 3: 'rd' }[generation % 10] || 'th');
      series = `Intel ${generation}${suffix} Gen`;
    } else if (coreNew) {
      family = `Core ${coreNew[1]}`;
      series = `Intel Core ${Math.floor(Number(coreNew[2]) / 100) * 100} Series`;
    } else {
      family = label.match(/\b(Athlon|Pentium|Celeron|Xeon)\b/i)?.[1] || family;
    }
    return { manufacturer, family, series };
  }

  function active(state) {
    return groups.some(([key]) => state[key]?.length);
  }

  function matches(option, state, except) {
    const facts = classify(option);
    return groups.every(([key]) => key === except || !state[key]?.length || state[key].includes(facts[key]));
  }

  function choices(options, state, key) {
    const values = new Set([...options.map(option => classify(option)[key]), ...(state[key] || [])]);
    if (key === 'manufacturer') { values.add('AMD'); values.add('Intel'); }
    return [...values].sort(compare).map(value => ({
      value,
      selected: Boolean(state[key]?.includes(value)),
      count: options.filter(option => classify(option)[key] === value && matches(option, state, key)).length
    }));
  }

  function render(document, options, state, expanded, onChange) {
    const fragment = document.createDocumentFragment();
    for (const [key, title] of groups) {
      const details = document.createElement('details');
      details.setAttribute('data-elite-cpu-facet', key);
      details.open = expanded.has(key) ? expanded.get(key) : true;
      const summary = document.createElement('summary');
      const heading = document.createElement('span');
      heading.textContent = title;
      const clear = document.createElement('button');
      clear.type = 'button';
      clear.textContent = 'Clear';
      clear.setAttribute('data-elite-cpu-facet-clear', key);
      clear.setAttribute('aria-label', `Clear ${title.toLowerCase()}`);
      clear.disabled = !state[key]?.length;
      clear.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();
        state[key] = [];
        onChange();
      });
      summary.append(heading, clear);
      const list = document.createElement('div');
      list.setAttribute('data-elite-cpu-facet-choices', '');
      for (const choice of choices(options, state, key)) {
        const row = document.createElement('label');
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.checked = choice.selected;
        input.disabled = !choice.count && !choice.selected;
        input.value = choice.value;
        input.setAttribute('data-elite-cpu-facet-option', key);
        input.setAttribute('aria-label', choice.value);
        input.addEventListener('change', () => {
          const selected = new Set(state[key] || []);
          if (input.checked) selected.add(choice.value);
          else selected.delete(choice.value);
          state[key] = [...selected];
          onChange();
        });
        const label = document.createElement('span');
        label.textContent = choice.value;
        const count = document.createElement('span');
        count.setAttribute('data-elite-cpu-facet-count', '');
        count.textContent = `(${choice.count})`;
        count.setAttribute('aria-label', `${choice.count} matching processor models`);
        row.append(input, label, count);
        list.append(row);
      }
      details.append(summary, list);
      fragment.append(details);
    }
    return fragment;
  }

  const api = { classify, matches, choices, active, render };
  if (typeof module === 'object' && module.exports) module.exports = api;
  else scope.EliteCPUFilterFacets = api;
})(typeof window === 'object' ? window : {});
